import clsx from "clsx";
import { Paragraphs, Texture } from "components";
import { useOnOverflow, useScreenResize } from "hooks";
import { QuoteIcon } from "images-react";
import { useEffect, useRef, useState } from "react";
import "styles/sections/reviews/SlideItem.scss";
import { LangProp, Review } from "types";

// logos
import danone from "images-react/logos/danone.png";
import naturhouse from "images-react/logos/naturhouse.png";
import salonyAgata from "images-react/logos/salony-agata.png";

const images: { [key: string]: string } = {
  danone,
  salonyAgata,
  naturhouse,
};

function SlideItem({ data, slideNumber, currentSlide, label, lang }: Props) {
  const isMobile = useScreenResize(688);
  const { content, author, company } = data;
  const imageSrc = images[company.logo];
  const isCurrentSlide = slideNumber === currentSlide;

  // calculate slide's transform value
  let distanceToMiddle = (slideNumber - currentSlide) * 100;
  const additionalSpace = isMobile ? 3 : 18.97;
  if (distanceToMiddle < 0) {
    distanceToMiddle = distanceToMiddle - additionalSpace;
  } else if (distanceToMiddle > 0) {
    distanceToMiddle = distanceToMiddle + additionalSpace;
  }

  const calculatedStyle = {
    transform: `translateX(${distanceToMiddle}%)`,
  };

  // check if review's content fits inside the container
  const contentRef = useRef(null);
  const contentIsOverflown = useOnOverflow(contentRef, [lang]);

  const [contentIsExpanded, setContentIsExpanded] = useState(false);
  useEffect(() => {
    if (!isCurrentSlide) setContentIsExpanded(false);
  }, [isCurrentSlide]);
  useEffect(() => {
    setContentIsExpanded(false);
  }, [lang]);
  const moreButtonLabel = contentIsExpanded
    ? `${label.less} -`
    : `${label.more} +`;
  const contentId = "review-" + slideNumber;

  return (
    <li className="slide" style={calculatedStyle}>
      <Texture className="slide__background" />
      <blockquote className="slide__blockquote">
        <div className="slide__content">
          <QuoteIcon
            className="slide__quote-icon"
            role="presentation"
            aria-hidden="true"
          />
          <div className="slide__paragraphs-container">
            <div
              className={clsx(
                "slide__paragraphs-expand",
                contentIsExpanded && "slide__paragraphs-expand--expanded",
                contentIsOverflown && "slide__paragraphs-expand--overflown"
              )}
              ref={contentRef}
              id={contentId}
            >
              <Paragraphs>{content}</Paragraphs>
              <button
                className={clsx(
                  "slide__more-button",
                  contentIsExpanded && "slide__more-button--expanded"
                )}
                disabled={!contentIsOverflown}
                onClick={() => setContentIsExpanded((c) => !c)}
                aria-controls={contentId}
                aria-expanded={contentIsExpanded}
                tabIndex={contentIsOverflown && isCurrentSlide ? undefined : -1}
              >
                {moreButtonLabel}
              </button>
            </div>
          </div>
        </div>

        <footer className="slide__footer">
          <div className="slide__footer-item">
            <p className="slide__author">{author.fullName}</p>
            <p className="slide__role">{author.role}</p>
          </div>

          <div className="slide__footer-item">
            <img className="slide__logo" src={imageSrc} alt={company.name} />
          </div>
        </footer>
      </blockquote>
    </li>
  );
}

interface Props extends LangProp {
  data: Review;
  slideNumber: number;
  currentSlide: number;
  label: { more: string; less: string };
}

export default SlideItem;
